// import

import {styled} from 'goober';
import type {ComponentProps, ReactChild} from 'react';

import type {NodePage} from 'libs/db';
import type {PostNode, BlogMainNode, CtaNode, UrlNode} from 'libs/model';
import {ContentSidebar} from 'modules/App';
import {Link} from 'modules/Link';
import {withPaging} from 'modules/Seo';
import {Type} from 'modules/Type';

import {BlogMainSeo} from './BlogMainSeo';
import {Pagination} from './Pagination';
import {Post} from './Post';
import {Postlet} from './Postlet';
import {SignupHeader} from './SignupHeader';
import {SignupWidget} from './SignupWidget';

// types

type Props = ComponentProps<typeof Styled> & {
  main: BlogMainNode;
  header?: ReactChild;
  area?: UrlNode;
  cta?: CtaNode;
  posts?: PostNode[];
  page?: NodePage<PostNode>;
  small?: boolean;
};

// component

export function BlogPage(props: Props) {
  const {
    main, small,
    cta = main.meta.headerCtaRef,
    page, posts, header, area,
    ...rest
  } = props;
  const {meta: {sidebarCtaRef: signup}} = main;
  const El = small ? Postlet : Post;

  return (
    <Styled {...rest}>
      <BlogMainSeo
        area={area}
        main={main}
        page={withPaging(page) ? page : undefined}
        posts={posts}
      />

      {cta ? (
        <SignupHeader cta={cta} />
      ) : null}

      <ContentSidebar
        content={(
          <>
            {header }

            {posts?.length ? null : (
              <aside className="notFound">
                <Type>
                  <h2>Oh, no!</h2>
                  <p>We can't find any posts here. Maybe you'd better head back to the <Link href="/blog">blog</Link> and see some of our latest tips?</p>
                </Type>
              </aside>
            )}

            {posts?.map((post) => (
              <El
                key={post.meta.slug}
                className="post"
                post={post}
              />
            ))}

            {withPaging(page) ? (
              <Pagination
                className="pagination"
                last={page.last}
                page={page.page}
              />
            ) : null}
          </>
        )}
        sidebar={signup ? (
          <SignupWidget
            blurb={signup.body}
            buttonText={signup.meta.buttonText}
            title={signup.meta.headline}
          />
        ) : null}
      />
    </Styled>
  );
}

// styles

const Styled = styled('div')`
  .post + * { margin-top: 6rem; }
  .postlet + .postlet { margin-top: 1.5rem; }
  .pagination { margin-top: 6rem; }

  .notFound {
    position: relative;
    background: var(--white);
    margin: 0 2rem;
    padding: 3.5rem 4.5rem 4.5rem;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -2rem;
      right: -2rem;
      bottom: 0;
      z-index: -1;
      border: 0.75rem solid var(--gray-50);
      border-radius: 0.5rem;
    }
  }
`;
